import {
  BIOMES,
  BOUNTIES,
  type BountiesEvent,
  type BountyClaimed,
  type BountyRejected,
  type BountyView,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { bountyClaimedText, bountyRefusalText, bountyStateText } from './bounty-text.js';
import { countdown } from './contract-text.js';
import { closeButton } from './hud.js';
import { hideItemHover } from './item-hover.js';
import { element, kitIcon, kitTitle } from './kit.js';
import { itemSlot } from './quest-rewards.js';
import { uiFormat, uiText } from './text.js';
import './smith.css';
import './bench.css';
import './quests.css';
import './bounty.css';

/**
 * A village's wanted board (ADR 0096): the day's bounties of its region,
 * each with its notice, the place the beast was seen, what it pays and
 * where the hunt stands, « Prendre » while open and « Toucher » once slain,
 * and how long until the next ones. Only a view of what the server sent:
 * taking and claiming are intentions it judges. Closed by the cross, by
 * Escape, or by walking away.
 */

export interface BountyPanelOptions {
  icons: () => CssAtlas | null;
  onTake: (boardId: string, slot: string) => void;
  onClaim: (boardId: string, slot: string) => void;
}

/** The countdown is in minutes: a redraw every half-minute keeps it true. */
const COUNTDOWN_REFRESH_MS = 30_000;

export class BountyPanel {
  private readonly root: HTMLElement;
  private readonly renew: HTMLElement;
  private readonly list: HTMLElement;
  private readonly message: HTMLElement;
  private board: string | undefined;
  private state: BountiesEvent | undefined;
  /** When the state arrived, for the countdown. */
  private receivedAt = 0;
  private timer: number | undefined;
  /** Between a click and the server's answer: one click, one request. */
  private pending = false;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.root.hidden) this.close();
  };

  constructor(private readonly options: BountyPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'smith bounty';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Avis de recherche'));

    const title = document.createElement('h2');
    title.className = 'smith__title';
    kitTitle(title, 'quests');
    title.textContent = uiText('Avis de recherche');
    title.append(
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );

    this.renew = element('p', 'bounty__renew');
    this.list = element('div', 'bench__list bounty__list');
    this.message = element('p', 'smith__message bounty__message');
    this.message.setAttribute('aria-live', 'polite');

    this.root.append(title, this.renew, this.list, this.message);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKey);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** The board read, while the window is open. */
  get boardId(): string | undefined {
    return this.isOpen ? this.board : undefined;
  }

  /** Opened at a board: its bounties come back from the server. */
  open(boardId: string): void {
    if (this.board !== boardId) this.state = undefined;
    this.board = boardId;
    this.pending = false;
    this.root.hidden = false;
    this.say('', 'success');
    if (this.timer !== undefined) window.clearInterval(this.timer);
    this.timer = window.setInterval(() => {
      this.render();
    }, COUNTDOWN_REFRESH_MS);
    this.render();
  }

  close(): void {
    if (this.root.hidden) return;
    this.root.hidden = true;
    this.pending = false;
    if (this.timer !== undefined) window.clearInterval(this.timer);
    this.timer = undefined;
    hideItemHover();
  }

  update(state: BountiesEvent): void {
    if (state.boardId !== this.board) return;
    this.state = state;
    this.receivedAt = performance.now();
    this.pending = false;
    this.render();
  }

  showClaimed(claimed: BountyClaimed): void {
    this.pending = false;
    this.say(bountyClaimedText(claimed), 'success');
    this.render();
  }

  showRejected(rejected: BountyRejected): void {
    this.pending = false;
    this.say(bountyRefusalText(rejected, this.state?.biome), 'refused');
    this.render();
  }

  destroy(): void {
    if (this.timer !== undefined) window.clearInterval(this.timer);
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }

  private say(text: string, tone: 'success' | 'refused'): void {
    this.message.textContent = text;
    this.message.dataset.tone = tone;
  }

  private render(): void {
    if (this.root.hidden) return;
    hideItemHover();
    const scroll = this.list.scrollTop;
    this.list.replaceChildren();
    const state = this.state;
    if (state === undefined) {
      this.renew.textContent = '';
      this.list.append(element('p', 'smith__muted', 'Tu lis les avis placardés…'));
      return;
    }
    const left = state.dayEndsInMs - (performance.now() - this.receivedAt);
    this.renew.textContent = uiFormat('{village} · nouveaux avis dans {time}', {
      village: uiText(BIOMES[state.biome].village),
      time: countdown(left),
    });
    if (state.bounties.length === 0) {
      this.list.append(element('p', 'smith__muted', 'Aucun avis aujourd’hui.'));
    }
    for (const bounty of state.bounties) this.list.append(this.card(bounty));
    this.list.scrollTop = scroll;
  }

  private card(bounty: BountyView): HTMLElement {
    const template = BOUNTIES[bounty.mob];
    const card = element('article', `bounty__card bounty__card--${bounty.state}`);
    card.dataset.slot = bounty.slot;

    const head = element('div', 'bounty__head');
    head.append(element('h3', 'bounty__name', template.name));
    head.append(element('span', 'bounty__state', bountyStateText(bounty.state)));
    card.append(head);
    card.append(element('p', 'bounty__notice', template.text));
    card.append(
      element(
        'p',
        'bounty__place',
        uiFormat('Vue près de : {place}', { place: uiText(bounty.near) }),
      ),
    );

    const foot = element('div', 'bounty__foot');
    const reward = element('div', 'contracts__reward bounty__reward');
    if (bounty.reward.gold > 0) {
      const gold = element('span', 'quest-rewards__gold', String(bounty.reward.gold));
      gold.prepend(kitIcon('gold'));
      reward.append(gold);
    }
    for (const item of bounty.reward.items) reward.append(itemSlot(item, this.options.icons()));
    foot.append(reward);

    const action = this.action(bounty);
    if (action !== undefined) foot.append(action);
    card.append(foot);
    return card;
  }

  /** « Prendre » on an open bounty, « Toucher » on a slain one; nothing otherwise. */
  private action(bounty: BountyView): HTMLButtonElement | undefined {
    const board = this.board;
    if (board === undefined) return undefined;
    if (bounty.state !== 'open' && bounty.state !== 'done') return undefined;
    const take = bounty.state === 'open';
    const button = element('button', 'smith__go bounty__go', take ? 'Prendre' : 'Toucher');
    button.type = 'button';
    button.disabled = this.pending;
    button.addEventListener('click', () => {
      if (this.pending) return;
      this.pending = true;
      for (const other of this.list.querySelectorAll<HTMLButtonElement>('.bounty__go')) {
        other.disabled = true;
      }
      this.say('', 'success');
      if (take) this.options.onTake(board, bounty.slot);
      else this.options.onClaim(board, bounty.slot);
    });
    return button;
  }
}
